import { createContext, useContext, ReactNode, useState, useMemo, useCallback, useEffect } from "react";

interface QuizResult {
  courseId: string;
  moduleId: string;
  score: number;
  passed: boolean;
  completedAt: Date;
}

interface QuizContextType {
  quizResults: { [key: string]: QuizResult };
  saveQuizResult: (courseId: string, moduleId: string, score: number, passed: boolean) => void;
  getQuizResult: (courseId: string, moduleId: string) => QuizResult | undefined;
  resetCourse: (courseId: string) => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

const QUIZ_RESULTS_STORAGE_KEY = 'jade_quiz_results';

const loadQuizResultsFromStorage = (): { [key: string]: QuizResult } => {
  try {
    const stored = localStorage.getItem(QUIZ_RESULTS_STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    
    Object.keys(parsed).forEach(key => {
      if (parsed[key].completedAt) {
        parsed[key].completedAt = new Date(parsed[key].completedAt);
      }
    });
    
    return parsed;
  } catch (error) {
    return {};
  }
};

const saveQuizResultsToStorage = (results: { [key: string]: QuizResult }): void => {
  try {
    localStorage.setItem(QUIZ_RESULTS_STORAGE_KEY, JSON.stringify(results));
  } catch (error) {
  }
};

export function QuizProvider({ children }: { children: ReactNode }) {
  const [quizResults, setQuizResults] = useState<{ [key: string]: QuizResult }>(() => 
    loadQuizResultsFromStorage()
  );

  useEffect(() => {
    saveQuizResultsToStorage(quizResults);
  }, [quizResults]);

  const saveQuizResult = useCallback((courseId: string, moduleId: string, score: number, passed: boolean) => {
    const key = `${courseId}-${moduleId}`;
    setQuizResults(prev => ({
      ...prev,
      [key]: {
        courseId,
        moduleId,
        score,
        passed,
        completedAt: new Date()
      }
    }));
  }, []);

  const getQuizResult = useCallback((courseId: string, moduleId: string): QuizResult | undefined => {
    const key = `${courseId}-${moduleId}`;
    return quizResults[key];
  }, [quizResults]);

  const resetCourse = useCallback((courseId: string) => {
    setQuizResults(prev => {
      const newResults = { ...prev };
      Object.keys(newResults).forEach(key => {
        if (key.startsWith(`${courseId}-`)) {
          delete newResults[key];
        }
      });
      return newResults;
    });
  }, []);

  const value = useMemo(
    () => ({ quizResults, saveQuizResult, getQuizResult, resetCourse }),
    [quizResults, saveQuizResult, getQuizResult, resetCourse]
  );

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
}

export function useQuiz() {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error("useQuiz must be used within a QuizProvider");
  }
  return context;
}
